// src/utils/brouillonNcs.ts — SCE Issues
// Brouillon d'une déclaration NC-S, gardé dans le navigateur (06/10, retour
// de Guillaume : « la fenêtre part trop facilement, le travail est perdu »).
//
// Un brouillon par problème. Il s'écrit à chaque saisie, reste quand on
// « réduit » la fenêtre (ou qu'elle se ferme par accident), et revient à la
// réouverture. Il s'efface quand la NC-S est envoyée, quand on l'abandonne,
// ou au bout de 30 jours.
//
// Dans ce navigateur seulement (localStorage) : un brouillon commencé au
// téléphone ne se retrouve pas sur le PC.

import type { DeclarationNcs } from '@/utils/declarationNcs'

const PREFIXE = 'sce-issues:ncs-brouillon:'
const VERSION = 1
/** Au-delà, un brouillon oublié ne revient plus. */
export const DUREE_BROUILLON_MS = 30 * 86_400_000

interface BrouillonStocke {
  version: number
  /** Dernière modification, ISO. */
  majLe: string
  saisie: Omit<DeclarationNcs, 'dateNotification' | 'dateOccurrence'> & {
    dateNotification: string
    dateOccurrence: string
  }
}

export interface Brouillon {
  saisie: DeclarationNcs
  majLe: string
}

/** Texte à garder. */
export function serialiserBrouillon(saisie: DeclarationNcs, maintenant: Date): string {
  const b: BrouillonStocke = {
    version: VERSION,
    majLe: maintenant.toISOString(),
    saisie: {
      ...saisie,
      dateNotification: saisie.dateNotification.toISOString(),
      dateOccurrence: saisie.dateOccurrence.toISOString(),
    },
  }
  return JSON.stringify(b)
}

/** Relit un brouillon ; null s'il est illisible, d'une autre version ou trop vieux. */
export function lireBrouillon(texte: string | null, maintenant: Date): Brouillon | null {
  if (!texte) return null
  try {
    const b = JSON.parse(texte) as Partial<BrouillonStocke>
    if (b.version !== VERSION || !b.saisie || !b.majLe) return null
    if (maintenant.getTime() - Date.parse(b.majLe) > DUREE_BROUILLON_MS) return null
    const dateNotification = new Date(b.saisie.dateNotification)
    const dateOccurrence = new Date(b.saisie.dateOccurrence)
    if (Number.isNaN(dateNotification.getTime()) || Number.isNaN(dateOccurrence.getTime())) return null
    return { majLe: b.majLe, saisie: { ...b.saisie, dateNotification, dateOccurrence } as DeclarationNcs }
  } catch {
    return null
  }
}

/** La saisie diffère-t-elle du pré-remplissage ? (rien à garder sinon) */
export function saisieModifiee(saisie: DeclarationNcs, depart: DeclarationNcs): boolean {
  return serialiserBrouillon(saisie, new Date(0)) !== serialiserBrouillon(depart, new Date(0))
}

// ─── Le navigateur (jamais bloquant) ───

export function lireBrouillonNcs(issueId: string, maintenant = new Date()): Brouillon | null {
  try {
    return lireBrouillon(localStorage.getItem(PREFIXE + issueId), maintenant)
  } catch {
    return null
  }
}

export function ecrireBrouillonNcs(issueId: string, saisie: DeclarationNcs, maintenant = new Date()): void {
  try {
    localStorage.setItem(PREFIXE + issueId, serialiserBrouillon(saisie, maintenant))
  } catch {
    // Mémoire pleine ou interdite : la fenêtre reste ouverte, rien n'est perdu tant qu'elle l'est.
  }
  signaler()
}

export function effacerBrouillonNcs(issueId: string): void {
  try {
    localStorage.removeItem(PREFIXE + issueId)
  } catch {
    // rien à faire
  }
  signaler()
}

// ─── Tous les brouillons, et qui les suit (pastille « NC-S en cours », 06/10) ───

/** Émis quand un brouillon s'écrit, s'efface, ou qu'une fenêtre NC-S s'ouvre ou se ferme. */
export const EVENEMENT_BROUILLONS = 'sce:ncs-brouillons'

function signaler(): void {
  try {
    window.dispatchEvent(new Event(EVENEMENT_BROUILLONS))
  } catch {
    // hors navigateur (bancs d'essai)
  }
}

/** Les brouillons du navigateur, du plus récent au plus ancien. */
export function listerBrouillonsNcs(maintenant = new Date()): { issueId: string; brouillon: Brouillon }[] {
  const liste: { issueId: string; brouillon: Brouillon }[] = []
  try {
    for (let k = 0; k < localStorage.length; k++) {
      const cle = localStorage.key(k)
      if (!cle?.startsWith(PREFIXE)) continue
      const brouillon = lireBrouillon(localStorage.getItem(cle), maintenant)
      if (brouillon) liste.push({ issueId: cle.slice(PREFIXE.length), brouillon })
    }
  } catch {
    return []
  }
  return liste.sort((a, b) => b.brouillon.majLe.localeCompare(a.brouillon.majLe))
}

/** Fenêtres NC-S ouvertes en ce moment (leur brouillon n'est pas « en attente »). */
const fenetresOuvertes = new Set<string>()

export function noterFenetreNcs(issueId: string, ouverte: boolean): void {
  if (ouverte) fenetresOuvertes.add(issueId)
  else fenetresOuvertes.delete(issueId)
  signaler()
}

export function fenetreNcsOuverte(): boolean {
  return fenetresOuvertes.size > 0
}
